import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../../config/axios';
import { Button, Display } from '../../components/Atoms';
import { LoadingState, ErrorState } from '../../components/states';
import { useApi } from '../../lib/api';
import CareShell, { CareLabel, CareSection, careInputStyle } from './CareShell';

/**
 * CareAdd - formularz dodania zabiegu (spray lub fertilization).
 *
 * URL params:
 *   ?type=spray | ?type=fertilization (default: spray)
 *   ?bed=ID (opcjonalnie - jeśli przyszliśmy z konkretnego beda)
 *
 * Endpoint:
 *   POST /api/care - uniwersalny dla spray + fertilization
 */

const SPRAY_TYPES = [
  { v: 'fungicide',   l: 'Fungicyd' },
  { v: 'insecticide', l: 'Insektycyd' },
  { v: 'herbicide',   l: 'Herbicyd' },
  { v: 'acaricide',   l: 'Akarycyd' },
  { v: 'bactericide', l: 'Bakteriocyd' },
  { v: 'biological',  l: 'Biologiczny' },
  { v: 'natural',     l: 'Naturalny' },
  { v: 'other',       l: 'Inny' },
];

const FERT_TYPES = [
  { v: 'mineral', l: 'Mineralny (sztuczny, NPK)' },
  { v: 'organic', l: 'Organiczny (kompost, obornik)' },
  { v: 'natural', l: 'Naturalny (gnojówka, herbata kompostowa)' },
];

const APPLICATION_METHODS = [
  { v: 'soil',   l: 'Doglebowe' },
  { v: 'foliar', l: 'Dolistne' },
];

function calcSafeHarvestDate(actionDate, withdrawal) {
  if (!actionDate || !withdrawal) return null;
  const d = new Date(actionDate);
  d.setDate(d.getDate() + parseInt(withdrawal, 10));
  return d.toISOString().split('T')[0];
}

function calcNextDate(actionDate, freqDays) {
  if (!actionDate || !freqDays) return null;
  const d = new Date(actionDate);
  d.setDate(d.getDate() + parseInt(freqDays, 10));
  return d.toISOString().split('T')[0];
}

// ── Widget pogody pod inputem daty ──────────────────────────────
// Sprawdza prognozę dla wybranego dnia i pokazuje rekomendacje:
// dla oprysku — wiatr/temp/opady muszą być w optimum.
function WeatherCheck({ date, actionType }) {
  const [forecast, setForecast] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setForecast(null);
    setError(null);
    axios.get('/api/weather/forecast')
      .then(r => { if (!cancelled) setForecast(r.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || 'Brak prognozy'); });
    return () => { cancelled = true; };
  }, []);

  if (!date) return null;

  if (error) {
    return (
      <div style={weatherBoxStyle('dim')}>
        <Meta>Pogoda</Meta>
        <span style={{ color: 'var(--t-text-dim)', fontSize: 12 }}>
          {error === 'Lokalizacja nie ustawiona' ? 'Ustaw lokalizację w profilu, aby sprawdzać pogodę.' : error}
        </span>
      </div>
    );
  }

  if (!forecast || !Array.isArray(forecast.daily)) {
    return (
      <div style={weatherBoxStyle('dim')}>
        <Meta>Pogoda</Meta>
        <span style={{ color: 'var(--t-text-dim)', fontSize: 12 }}>Wczytuję prognozę…</span>
      </div>
    );
  }

  const day = forecast.daily.find(d => d.date === date);
  const today = new Date().toISOString().split('T')[0];
  const isPast = date < today;

  if (!day) {
    return (
      <div style={weatherBoxStyle('dim')}>
        <Meta>Pogoda</Meta>
        <span style={{ color: 'var(--t-text-dim)', fontSize: 12 }}>
          {isPast ? 'Zabieg w przeszłości — nie sprawdzam pogody.' : 'Prognoza dostępna na 5 dni do przodu.'}
        </span>
      </div>
    );
  }

  const issues = [];
  const isSpray = actionType === 'spray';

  if (isSpray) {
    if (day.tempAvg < 5) issues.push({ tone: 'rust', msg: `${day.tempAvg}°C — za zimno (min. 5°C)` });
    else if (day.tempAvg > 28) issues.push({ tone: 'amber', msg: `${day.tempAvg}°C — za gorąco (max. 28°C)` });
    if (day.maxWind > 18) issues.push({ tone: 'rust', msg: `Wiatr ${day.maxWind} km/h — za silny do oprysku` });
    else if (day.avgWind > 12) issues.push({ tone: 'amber', msg: `Wiatr ${day.avgWind} km/h — uważnie` });
    if (day.precipProbability > 50) issues.push({ tone: 'rust', msg: `Opady ${day.precipProbability}% — oprysk się zmyje` });
    else if (day.precipProbability > 30) issues.push({ tone: 'amber', msg: `Opady ${day.precipProbability}% — możliwe` });
  } else {
    // fertilization: tylko ekstrema
    if (day.precipProbability > 80) issues.push({ tone: 'amber', msg: `Opady ${day.precipProbability}% — nawóz może się wypłukać` });
    if (day.tempAvg < 0) issues.push({ tone: 'rust', msg: `${day.tempAvg}°C — mróz, nawożenie nieefektywne` });
  }

  const worst = issues.find(i => i.tone === 'rust') ? 'rust' : (issues[0]?.tone || 'sage');
  const ok = issues.length === 0;

  return (
    <div style={weatherBoxStyle(worst)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <Meta>Prognoza · {day.description}</Meta>
        <span style={{ fontFamily: 'var(--t-mono)', fontSize: 11, color: 'var(--t-text-dim)' }}>
          {day.tempMin}°/{day.tempMax}° · {day.avgWind} km/h · {day.precipProbability}% opady
        </span>
      </div>
      {ok ? (
        <div style={{ fontSize: 12, color: 'var(--t-sage)' }}>
          ✓ Dobre warunki do {isSpray ? 'oprysku' : 'nawożenia'}.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 12, color: 'var(--t-text)' }}>
          {issues.map((i, idx) => (
            <li key={idx} style={{ color: i.tone === 'rust' ? 'var(--t-rust)' : 'var(--t-amber)' }}>{i.msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function weatherBoxStyle(tone) {
  const colors = {
    sage:  { bg: 'rgba(168, 196, 158, 0.06)', bo: 'var(--t-sage)' },
    amber: { bg: 'rgba(214, 161, 96, 0.06)',  bo: 'var(--t-amber)' },
    rust:  { bg: 'rgba(192, 106, 74, 0.08)',  bo: 'var(--t-rust)' },
    dim:   { bg: 'var(--t-bg-elev)',          bo: 'var(--t-line-2)' },
  };
  const c = colors[tone] || colors.dim;
  return {
    marginTop: 8,
    padding: '10px 12px',
    borderRadius: 8,
    background: c.bg,
    borderLeft: `3px solid ${c.bo}`,
  };
}

function Meta({ children }) {
  return <span style={{
    fontFamily: 'var(--t-mono)', fontSize: 10,
    letterSpacing: '.16em', textTransform: 'uppercase',
    color: 'var(--t-text-mute)', display: 'block',
  }}>{children}</span>;
}

export default function CareAdd() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialType = searchParams.get('type') === 'fertilization' ? 'fertilization' : 'spray';
  const initialBedId = searchParams.get('bed') || '';
  const initialName = searchParams.get('name') || searchParams.get('spray_name') || '';
  const initialWithdrawal = parseInt(searchParams.get('withdrawal'), 10) || 0;
  const initialRepeatEvery = parseInt(searchParams.get('repeat_every'), 10) || 0;
  const initialNote = searchParams.get('note') || '';

  const { data: plots, loading, error } = useApi('/api/plots/all-with-details');

  const [actionType, setActionType] = useState(initialType);
  const [plotId, setPlotId] = useState('');
  const [bedId, setBedId] = useState(initialBedId);
  const [form, setForm] = useState({
    action_name: initialName,
    action_date: new Date().toISOString().split('T')[0],
    dosage: '',
    weather_conditions: '',
    note: initialNote,
    // Spray
    spray_type: 'fungicide',
    withdrawal_period: initialWithdrawal,
    // Fertilization
    fertilizer_type: 'mineral',
    npk_ratio: '',
    application_method: 'soil',
    is_recurring: initialRepeatEvery > 0,
    repeat_frequency: initialRepeatEvery > 0 ? initialRepeatEvery : 14,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionResult, setSuggestionResult] = useState('');
  const [suggestionError, setSuggestionError] = useState('');
  const [suggestionData, setSuggestionData] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);

  // Jeśli initialBedId podany, znajdź jego plotId
  useEffect(() => {
    if (initialBedId && plots) {
      for (const p of plots) {
        if ((p.beds || []).some(b => String(b.id) === String(initialBedId))) {
          setPlotId(String(p.id));
          break;
        }
      }
    }
  }, [initialBedId, plots]);

  if (error?.status === 401) {
    return <CareShell><ErrorState error={error} /></CareShell>;
  }
  if (loading) return <CareShell><LoadingState /></CareShell>;

  const allPlots = plots || [];
  const selectedPlot = allPlots.find(p => String(p.id) === String(plotId));
  const beds = selectedPlot?.beds || [];

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const safeHarvestPreview = actionType === 'spray' && form.action_date && form.withdrawal_period > 0
    ? calcSafeHarvestDate(form.action_date, form.withdrawal_period)
    : null;
  const nextDatePreview = actionType === 'fertilization' && form.is_recurring && form.action_date && form.repeat_frequency > 0
    ? calcNextDate(form.action_date, form.repeat_frequency)
    : null;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!bedId) {
      setSaveError('Wybierz grządkę.');
      return;
    }
    if (!form.action_name.trim()) {
      setSaveError('Podaj nazwę zabiegu.');
      return;
    }

    setSaving(true);
    setSaveError('');

    const payload = {
      bed_id: parseInt(bedId, 10),
      action_type: actionType,
      action_name: form.action_name.trim(),
      action_date: form.action_date,
      dosage: form.dosage.trim() || undefined,
      weather_conditions: form.weather_conditions.trim() || undefined,
      note: form.note.trim() || undefined,
    };

    if (actionType === 'spray') {
      payload.withdrawal_period = parseInt(form.withdrawal_period, 10) || 0;
    } else {
      payload.fertilizer_type = form.fertilizer_type;
      payload.npk_ratio = form.npk_ratio.trim() || undefined;
      payload.application_method = form.application_method;
      payload.is_recurring = !!form.is_recurring;
      if (form.is_recurring) {
        payload.repeat_frequency = parseInt(form.repeat_frequency, 10);
      }
    }

    try {
      await axios.post('/api/care', payload);
      navigate(actionType === 'spray' ? '/care' : '/care/fertilization');
    } catch (err) {
      setSaveError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Nie udało się zapisać.');
      setSaving(false);
    }
  };

  return (
    <CareShell
      eyebrow="Nowy zabieg"
      title="Dodaj oprysk lub nawożenie"
      subtitle="Najpierw wybierz typ zabiegu i grządkę, potem szczegóły. Daty i karencja policzą się automatycznie."
    >
      <form onSubmit={handleSubmit}>
        {/* Typ zabiegu - 2 karty */}
        <CareSection title="Typ zabiegu">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { v: 'spray',         l: 'Oprysk',     d: 'Ochrona roślin (fungicydy, insektycydy)' },
              { v: 'fertilization', l: 'Nawożenie',  d: 'Odżywianie (mineralne, organiczne)' },
            ].map(opt => {
              const on = actionType === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setActionType(opt.v)}
                  style={{
                    padding: '20px 22px', borderRadius: 14,
                    background: on ? 'rgba(168, 196, 158, 0.08)' : 'var(--t-bg-elev)',
                    border: `1px solid ${on ? 'var(--t-sage)' : 'var(--t-line)'}`,
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 200ms ease',
                  }}
                >
                  <Display size={18} style={{
                    display: 'block', marginBottom: 6,
                    color: on ? 'var(--t-text)' : 'var(--t-text-dim)',
                  }}>{opt.l}</Display>
                  <div style={{
                    fontSize: 12, color: 'var(--t-text-dim)', lineHeight: 1.45,
                  }}>{opt.d}</div>
                </button>
              );
            })}
          </div>
        </CareSection>

        {/* Wybór grządki */}
        <CareSection title="Gdzie">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <CareLabel htmlFor="plot">Działka</CareLabel>
              <select
                id="plot"
                value={plotId}
                onChange={e => { setPlotId(e.target.value); setBedId(''); }}
                style={{ ...careInputStyle, colorScheme: 'dark' }}
              >
                <option value="">— wybierz działkę —</option>
                {allPlots.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <CareLabel htmlFor="bed">Grządka</CareLabel>
              <select
                id="bed"
                value={bedId}
                onChange={e => { setBedId(e.target.value); setSuggestionResult(''); setSuggestionError(''); }}
                disabled={!plotId}
                style={{ ...careInputStyle, colorScheme: 'dark', opacity: plotId ? 1 : 0.5 }}
              >
                <option value="">— wybierz grządkę —</option>
                {beds.map(b => (
                  <option key={b.id} value={b.id}>
                    Rząd {b.row_number} — {b.plant_name}{b.plant_variety ? ` (${b.plant_variety})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CareSection>

        {/* Wspólne pola */}
        <CareSection title="Szczegóły">
          <div style={{ marginBottom: 16 }}>
            <CareLabel htmlFor="action_name">Nazwa zabiegu (np. preparat)</CareLabel>
            <input
              id="action_name"
              type="text"
              value={form.action_name}
              onChange={e => setField('action_name', e.target.value)}
              placeholder={actionType === 'spray' ? 'np. Topas 100 EC' : 'np. Azofoska wiosenna'}
              maxLength={100}
              required
              style={careInputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <CareLabel htmlFor="action_date">Data zabiegu</CareLabel>
              <input
                id="action_date"
                type="date"
                value={form.action_date}
                onChange={e => setField('action_date', e.target.value)}
                required
                style={{ ...careInputStyle, colorScheme: 'dark' }}
              />
              <WeatherCheck date={form.action_date} actionType={actionType} />
            </div>
            <div>
              <CareLabel htmlFor="dosage">Dawka (opcjonalna)</CareLabel>
              <input
                id="dosage"
                type="text"
                value={form.dosage}
                onChange={e => setField('dosage', e.target.value)}
                placeholder="np. 5 ml/l, 1 g/m²"
                maxLength={100}
                style={careInputStyle}
              />
            </div>
          </div>

          {actionType === 'spray' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <CareLabel htmlFor="spray_type">Typ oprysku</CareLabel>
                <select
                  id="spray_type"
                  value={form.spray_type}
                  onChange={e => setField('spray_type', e.target.value)}
                  style={{ ...careInputStyle, colorScheme: 'dark' }}
                >
                  {SPRAY_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <CareLabel htmlFor="withdrawal">Karencja (dni)</CareLabel>
                <input
                  id="withdrawal"
                  type="number"
                  min={0}
                  max={365}
                  value={form.withdrawal_period}
                  onChange={e => setField('withdrawal_period', e.target.value)}
                  style={careInputStyle}
                />
              </div>
            </div>
          )}

          {actionType === 'fertilization' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <CareLabel htmlFor="fert_type">Typ nawozu</CareLabel>
                  <select
                    id="fert_type"
                    value={form.fertilizer_type}
                    onChange={e => setField('fertilizer_type', e.target.value)}
                    style={{ ...careInputStyle, colorScheme: 'dark' }}
                  >
                    {FERT_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
                <div>
                  <CareLabel htmlFor="npk">NPK (opcjonalnie)</CareLabel>
                  <input
                    id="npk"
                    type="text"
                    value={form.npk_ratio}
                    onChange={e => setField('npk_ratio', e.target.value)}
                    placeholder="np. 10-10-20"
                    maxLength={20}
                    style={careInputStyle}
                  />
                </div>
              </div>

              {/* Doradca nawożenia: przycisk + sugestia */}
              <div style={{ marginBottom: 16 }}>
                <button
                  type="button"
                  disabled={!bedId || suggesting}
                  onClick={async () => {
                    setSuggestionError('');
                    setSuggestionResult('');
                    setSuggestionData(null);
                    setSuggesting(true);
                    try {
                      const res = await axios.get(`/api/care/fertilizer-suggestion?bed_id=${bedId}`);
                      const { suggestion, info } = res.data;
                      if (suggestion) {
                        setSuggestionData(suggestion);
                        setField('action_name', suggestion.fertilizer.name);
                        setField('fertilizer_type', suggestion.fertilizer.type);
                        setField('npk_ratio', suggestion.fertilizer.npkRatio);
                        setField('dosage', `${suggestion.dosage} ${suggestion.unit}`);
                        setField('application_method', suggestion.applicationMethod);
                        if (suggestion.frequencyDays) {
                          setField('is_recurring', true);
                          setField('repeat_frequency', suggestion.frequencyDays);
                        }
                        setSuggestionResult(`✓ ${suggestion.fertilizer.name} — ${suggestion.dosage} ${suggestion.unit}${suggestion.notes ? ' (' + suggestion.notes + ')' : ''}`);
                      } else {
                        setSuggestionData(null);
                        setSuggestionResult(info || 'Brak sugestii dla tej rośliny.');
                      }
                    } catch (err) {
                      setSuggestionError(err.response?.data?.error || 'Nie udało się pobrać sugestii.');
                    } finally {
                      setSuggesting(false);
                    }
                  }}
                  style={{
                    padding: '10px 18px', borderRadius: 10,
                    background: 'var(--t-bg-elev)',
                    border: '1px solid var(--t-line)',
                    color: 'var(--t-text)',
                    fontFamily: 'var(--t-sans)', fontSize: 13,
                    cursor: bedId ? 'pointer' : 'not-allowed',
                    opacity: bedId ? 1 : 0.5,
                    transition: 'all 200ms ease',
                  }}
                >{suggesting ? 'Szukam…' : '🔍 Zaproponuj nawóz'}</button>
                {suggestionResult && (
                  <div style={{
                    marginTop: 8, padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(168, 196, 158, 0.08)',
                    borderLeft: '3px solid var(--t-sage)',
                    fontSize: 12, color: 'var(--t-text)', lineHeight: 1.5,
                  }}>{suggestionResult}</div>
                )}
                {/* Przycisk „Dodaj jako cykliczne zadanie" (T8 dokończenie) */}
                {suggestionResult && suggestionData && !suggestionData.info && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      disabled={creatingTask}
                      onClick={async () => {
                        setCreatingTask(true);
                        try {
                          const payload = {
                            bed_id: parseInt(bedId, 10),
                            fertilizer_name: suggestionData.fertilizer.name,
                            dosage: suggestionData.dosage ? `${suggestionData.dosage} ${suggestionData.unit}` : undefined,
                            frequency_days: suggestionData.frequencyDays || 14,
                          };
                          await axios.post('/api/care/fertilizer-task', payload);
                          setSuggestionResult(`✓ ${suggestionData.fertilizer.name} — cykliczne zadanie utworzone (co ${payload.frequency_days} dni)`);
                          setCreatingTask(false);
                        } catch (err) {
                          setSuggestionError(err.response?.data?.error || 'Nie udało się utworzyć zadania.');
                          setCreatingTask(false);
                        }
                      }}
                      style={{
                        padding: '8px 14px', borderRadius: 8,
                        background: 'rgba(168, 196, 158, 0.1)',
                        border: '1px solid var(--t-sage)',
                        color: 'var(--t-sage)',
                        fontFamily: 'var(--t-sans)', fontSize: 12,
                        cursor: creatingTask ? 'wait' : 'pointer',
                        transition: 'all 200ms ease',
                      }}
                    >{creatingTask ? '…' : '↻ Dodaj jako cykliczne zadanie'}</button>
                  </div>
                )}
                {suggestionError && (
                  <div style={{
                    marginTop: 8, padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(192, 106, 74, 0.08)',
                    borderLeft: '3px solid var(--t-rust)',
                    fontSize: 12, color: 'var(--t-text)',
                  }}>{suggestionError}</div>
                )}
              </div>

              <div style={{ marginBottom: 16 }}>
                <CareLabel htmlFor="method">Metoda aplikacji</CareLabel>
                <select
                  id="method"
                  value={form.application_method}
                  onChange={e => setField('application_method', e.target.value)}
                  style={{ ...careInputStyle, colorScheme: 'dark' }}
                >
                  {APPLICATION_METHODS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', borderRadius: 10,
                background: 'var(--t-bg-elev)',
                border: '1px solid var(--t-line)',
                marginBottom: 16,
              }}>
                <input
                  id="is_recurring"
                  type="checkbox"
                  checked={form.is_recurring}
                  onChange={e => setField('is_recurring', e.target.checked)}
                  style={{ accentColor: 'var(--t-sage)' }}
                />
                <label htmlFor="is_recurring" style={{
                  flex: 1, fontSize: 14, color: 'var(--t-text)', cursor: 'pointer',
                }}>Cykliczne (powtarzaj automatycznie)</label>
                {form.is_recurring && (
                  <input
                    type="number"
                    aria-label="Co ile dni powtarzać"
                    min={1}
                    max={365}
                    value={form.repeat_frequency}
                    onChange={e => setField('repeat_frequency', e.target.value)}
                    style={{
                      width: 70, padding: '6px 10px', borderRadius: 8,
                      background: 'var(--t-bg)', border: '1px solid var(--t-line-2)',
                      color: 'var(--t-text)', fontFamily: 'var(--t-mono)',
                      fontSize: 13, textAlign: 'right',
                    }}
                  />
                )}
                {form.is_recurring && (
                  <span style={{ fontSize: 12, color: 'var(--t-text-dim)' }}>dni</span>
                )}
              </div>
            </>
          )}

          <div style={{ marginBottom: 16 }}>
            <CareLabel htmlFor="weather">Warunki pogodowe (opcjonalnie)</CareLabel>
            <input
              id="weather"
              type="text"
              value={form.weather_conditions}
              onChange={e => setField('weather_conditions', e.target.value)}
              placeholder="np. słonecznie, 18°C, bezwietrznie"
              maxLength={100}
              style={careInputStyle}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <CareLabel htmlFor="note">Notatka (opcjonalna)</CareLabel>
            <textarea
              id="note"
              value={form.note}
              onChange={e => setField('note', e.target.value)}
              placeholder="Obserwacje, efekty, problemy…"
              maxLength={500}
              rows={3}
              style={{ ...careInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }}
            />
            <div style={{
              marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)',
              fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
              textAlign: 'right',
            }}>{form.note.length} / 500</div>
          </div>

          {/* Live preview safe harvest / next date */}
          {safeHarvestPreview && (
            <div style={{
              padding: '12px 14px', borderRadius: 10,
              background: 'rgba(168, 196, 158, 0.08)',
              borderLeft: '3px solid var(--t-sage)',
              fontSize: 13, color: 'var(--t-text)',
              marginBottom: 16,
            }}>
              📅 Bezpieczny zbiór od: <strong>{safeHarvestPreview}</strong> ({form.withdrawal_period} dni karencji)
            </div>
          )}
          {nextDatePreview && (
            <div style={{
              padding: '12px 14px', borderRadius: 10,
              background: 'rgba(220, 180, 100, 0.08)',
              borderLeft: '3px solid var(--t-amber)',
              fontSize: 13, color: 'var(--t-text)',
              marginBottom: 16,
            }}>
              ↻ Następna runda: <strong>{nextDatePreview}</strong> (co {form.repeat_frequency} dni)
            </div>
          )}
        </CareSection>

        {saveError && (
          <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: 'rgba(192, 106, 74, 0.08)',
            borderLeft: '3px solid var(--t-rust)',
            fontSize: 13, color: 'var(--t-text)', marginBottom: 22,
          }}>{saveError}</div>
        )}

        <div style={{
          display: 'flex', gap: 12, justifyContent: 'flex-end',
          padding: '16px 0', position: 'sticky', bottom: 0,
          background: 'var(--t-bg)',
          borderTop: '1px solid var(--t-line)',
        }}>
          <Button
            variant="ghost"
            onClick={() => navigate(actionType === 'spray' ? '/care' : '/care/fertilization')}
          >Anuluj</Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !bedId || !form.action_name.trim()}
          >{saving ? 'Zapisuję…' : 'Zapisz zabieg'}</Button>
        </div>
      </form>
    </CareShell>
  );
}
